// =============================================================================
// ui/state/commissions.tsx — the per-firm rate schedules.
// -----------------------------------------------------------------------------
// Loaded once at the shell, like contracts, because every surface that shows a
// net P&L depends on them (see selectors/derive.ts for why the stored `fees`
// column cannot be trusted).
//
// ⚠ NULL until a read succeeds, and consumers must WAIT rather than treat an
// empty schedule map as "no commissions configured". The two are
// indistinguishable in the output and the failure is the flattering one: no
// schedules means no fee is subtracted, so every net P&L reads better than the
// trader's real result. `io/sync/commissions.ts` throws on a failed read so this
// provider never has to guess which it is holding.
// =============================================================================

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { loadCommissionSchedules } from '../../io/sync/commissions';
import type { CommissionSchedules } from '../../selectors/derive';
import { reportFailure } from '../../io/diagnostics/errorLog';

export type CommissionsPhase = 'loading' | 'ready' | 'failed';

export interface CommissionsState {
  phase: CommissionsPhase;
  /** ⚠ NULL until a read succeeded. `{}` means "genuinely none configured". */
  schedules: CommissionSchedules | null;
  error: string | null;
  reload: () => void;
}

const Ctx = createContext<CommissionsState | null>(null);

export function useCommissions(): CommissionsState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useCommissions() outside <CommissionsProvider>');
  return v;
}

export function CommissionsProvider(
  { sb, children }: { sb: SupabaseClient; children: ReactNode },
) {
  const [phase, setPhase] = useState<CommissionsPhase>('loading');
  const [schedules, setSchedules] = useState<CommissionSchedules | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);

    void (async () => {
      try {
        const s = await loadCommissionSchedules(sb);
        if (cancelled) return;
        setSchedules(s);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        setError(reportFailure('commission schedules', e));
        setPhase('failed');
      }
    })();

    return () => { cancelled = true; };
  }, [sb, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const value = useMemo<CommissionsState>(
    () => ({ phase, schedules, error, reload }),
    [phase, schedules, error, reload],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
